import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { DEFAULT_SITE_CONTENT, SERVICES } from "../data/content";
import { mergeContent } from "../context/SiteContentContext";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Save } from "lucide-react";

const Section = ({ title, hint, children }) => (
  <div className="bg-white rounded-xl border border-[#E5EAE8] p-6">
    <h3 className="font-heading font-semibold text-slate-800">{title}</h3>
    {hint && <p className="text-xs text-slate-500 mt-0.5 mb-1">{hint}</p>}
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, textarea, rows = 3, testId }) => (
  <div>
    <label className="text-sm font-medium text-slate-700 block mb-1.5">{label}</label>
    {textarea ? (
      <Textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} rows={rows} className="rounded-xl text-sm" />
    ) : (
      <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-xl text-sm" />
    )}
  </div>
);

export const SiteContentEditor = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/site-content")
      .then(({ data }) => setContent(mergeContent(DEFAULT_SITE_CONTENT, data)))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  const set = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const setService = (id, key, value) =>
    setContent((c) => ({
      ...c,
      services: { ...c.services, [id]: { ...c.services[id], [key]: value } },
    }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/site-content", content);
      toast.success("Saved — changes are live on the website");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!content)
    return (
      <div className="flex justify-center py-10">
        <div className="w-6 h-6 border-4 border-[#5E8B7E] border-t-transparent rounded-full animate-spin" />
      </div>
    );

  return (
    <div className="space-y-6" data-testid="site-content-editor">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Edit the website text below. Changes apply to the live site as soon as you save.
        </p>
        <Button data-testid="content-save-btn" onClick={save} disabled={saving} className="rounded-full bg-[#2C557E] hover:bg-[#22436a]">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save All Changes"}
        </Button>
      </div>

      <Section title="Clinic Details" hint="Shown on the Home page, Contact page, footer and booking screens.">
        <Field label="Clinic Address" value={content.clinic.address} onChange={(v) => set("clinic", "address", v)} testId="content-clinic-address" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Phone Number" value={content.clinic.phone} onChange={(v) => set("clinic", "phone", v)} testId="content-clinic-phone" />
          <Field label="Clinic Hours" value={content.clinic.hours} onChange={(v) => set("clinic", "hours", v)} testId="content-clinic-hours" />
          <Field label="Online Consultation Hours" value={content.clinic.onlineHours} onChange={(v) => set("clinic", "onlineHours", v)} testId="content-clinic-online-hours" />
        </div>
      </Section>

      <Section title="Home Page — Hero" hint="The first thing visitors see.">
        <Field label="Badge (small label above the heading)" value={content.hero.badge} onChange={(v) => set("hero", "badge", v)} testId="content-hero-badge" />
        <Field label="Main Heading" value={content.hero.title} onChange={(v) => set("hero", "title", v)} testId="content-hero-title" />
        <Field label="Welcome Paragraph" textarea rows={4} value={content.hero.intro} onChange={(v) => set("hero", "intro", v)} testId="content-hero-intro" />
      </Section>

      <Section title="About Page — Introduction" hint="The three paragraphs at the top of the About page.">
        <Field label="Paragraph 1" textarea value={content.about.p1} onChange={(v) => set("about", "p1", v)} testId="content-about-p1" />
        <Field label="Paragraph 2" textarea rows={4} value={content.about.p2} onChange={(v) => set("about", "p2", v)} testId="content-about-p2" />
        <Field label="Paragraph 3" textarea rows={4} value={content.about.p3} onChange={(v) => set("about", "p3", v)} testId="content-about-p3" />
      </Section>

      <Section title="Services — Taglines & Descriptions" hint="Shown on the Services page cards and Home page previews.">
        {SERVICES.map((s) => (
          <div key={s.id} className="border border-[#EDF1EF] rounded-xl p-4 space-y-3">
            <div className="font-medium text-slate-800 text-sm">{s.name}</div>
            <Field label="Tagline" value={content.services[s.id]?.tagline || ""} onChange={(v) => setService(s.id, "tagline", v)} testId={`content-service-tagline-${s.id}`} />
            <Field label="Description" textarea rows={4} value={content.services[s.id]?.description || ""} onChange={(v) => setService(s.id, "description", v)} testId={`content-service-desc-${s.id}`} />
          </div>
        ))}
      </Section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#2C557E] hover:bg-[#22436a]">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save All Changes"}
        </Button>
      </div>
    </div>
  );
};
